import { DraftChip } from 'components/drafts/DraftChip';
import { SvgIconComponent } from 'components/icons';
import { ComponentProps, ReactNode, useContext } from 'react';
import { useIntl } from 'react-intl';
import { GlobalSearchEntityType } from 'types/globalSearch';
import { cn } from 'utils/cn';
import { GLOBAL_SEARCH_ENTITY_LABEL_ID } from './entityDisplay';
import { HighlightedText } from './HighlightedText';
import { EnterKeyCap, GlobalSearchRow, isPlainLeftClick, PaletteContext } from './GlobalSearchRow';

export type GlobalSearchBestBetRowProps = Omit<ComponentProps<'a'>, 'children' | 'href' | 'id' | 'title'> & {
    id: string;
    icon: SvgIconComponent;
    /** The record's family, which the palette names in place of the glyph. */
    entityType?: GlobalSearchEntityType;
    /** Plain text carrying the highlight sentinels, rendered through {@link HighlightedText}. */
    title: string;
    /** Marks the record as a draft, after its title. */
    draft?: boolean;
    secondary?: ReactNode;
    href: string;
    selected?: boolean;
    /** The pointer has come onto the row. It raises the peek card; it never moves the selection. */
    onHover?: () => void;
    onSelect: () => void;
};

/**
 * The exact-identifier hit, pinned above every section.
 *
 * It is a row of its own rather than a flagged {@link GlobalSearchRow} because nothing about it is
 * the same shape: a larger filled tile, a label saying why it is here, and a rule down its left
 * edge. There is at most one of these in a panel. The palette draws no such row, so there it is the
 * first plain row.
 */
export function GlobalSearchBestBetRow({
    id,
    icon: Icon,
    entityType,
    title,
    draft = false,
    secondary,
    href,
    selected = false,
    onHover,
    onSelect,
    className,
    onClick,
    onMouseEnter,
    ...rest
}: GlobalSearchBestBetRowProps) {
    const intl = useIntl();
    const palette = useContext(PaletteContext);

    if (palette) {
        return (
            <GlobalSearchRow
                {...rest}
                id={id}
                icon={Icon}
                entityType={entityType}
                title={
                    <>
                        {/* Outside any section, so no heading names the family to a screen reader: the row does. */}
                        {entityType && (
                            <span className="sr-only">
                                {intl.formatMessage({ id: GLOBAL_SEARCH_ENTITY_LABEL_ID[entityType] })},{' '}
                            </span>
                        )}
                        <HighlightedText text={title} />
                    </>
                }
                draft={draft}
                secondary={secondary}
                href={href}
                selected={selected}
                onHover={onHover}
                onSelect={onSelect}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                // Outside any section, so inset here as the sections inset theirs.
                className={cn('mx-2 mt-2', className)}
            />
        );
    }

    return (
        <a
            {...rest}
            id={id}
            href={href}
            role="option"
            aria-selected={selected}
            onMouseEnter={(event) => {
                onMouseEnter?.(event);
                onHover?.();
            }}
            onClick={(event) => {
                onClick?.(event);
                if (!isPlainLeftClick(event)) {
                    return;
                }
                event.preventDefault();
                onSelect();
            }}
            className={cn(
                'border-divider border-l-primary bg-primary-surface flex cursor-pointer items-center gap-3 border-b border-l-[3px] py-2.5 pr-3.5 pl-[11px] no-underline',
                selected && 'bg-primary-subtle',
                className
            )}
        >
            <span className="bg-primary text-grey-0 flex size-8 flex-none items-center justify-center rounded-[9px]">
                <Icon aria-hidden className="size-5" />
            </span>
            <span className="min-w-0 flex-1">
                <span className="text-primary text-caption new:text-label new:tracking-[0.06em] flex items-center gap-[5px] leading-[1.3333] font-semibold tracking-[0.04em] uppercase">
                    <span aria-hidden className="bg-secondary size-[5px] flex-none rounded-full" />
                    {intl.formatMessage({ id: 'global-search.exact-match' })}
                </span>
                <span className="text-grey-700 flex items-center gap-1.5 text-sm leading-[1.5715] font-semibold">
                    <span className="truncate">
                        <HighlightedText text={title} />
                    </span>
                    {draft ? <DraftChip className="flex-none" /> : null}
                </span>
                {secondary !== undefined && secondary !== null && (
                    <span className="text-grey-600 block truncate text-xs leading-[1.5]">{secondary}</span>
                )}
            </span>
            <EnterKeyCap visible={selected} />
        </a>
    );
}
